import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from 'translations/translator';
import {
  toggleShowLines,
  toggleShowBackground,
  clearAllPaths,
  enableContinuousDraw,
  setNextPathIndex,
} from 'state/action-creators/draw-stage';
import {
  togglePencilActivation,
  eraseAll,
} from 'state/action-creators/doodle-stage';
import {
  toggleMute,
  setVolume,
  setMute,
  setZoom,
  setTextSize,
} from 'state/action-creators/characters';
import PercentChooser from './percent-chooser';
import AudioManager from '../draw-stage/managers/audio-manager';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { faStop } from '@fortawesome/free-solid-svg-icons';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { faVolumeMute } from '@fortawesome/free-solid-svg-icons';
import { faVolumeDown } from '@fortawesome/free-solid-svg-icons';
import { faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { faSearchPlus } from '@fortawesome/pro-regular-svg-icons';
import { faSearchMinus } from '@fortawesome/pro-regular-svg-icons';
import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
const t = (key) => translate('lnf', key);
const tAria = (key, options?) => translate('a11y', key, options);

const LNFMenu = () => {
  const classes = useStyles();
  const {
    container,
    title,
    fieldSet,
    menuItems,
    menuItemTitle,
    menuItemSubtitle,
    buttonMenu,
    button,
    redButton,
    outlineButton,
    blueButton,
    buttonIcon,
    accessibilityMenuGroup,
    muteButton,
  } = classes;
  const dispatch = useDispatch();
  const {
    currentPathIndex,
    drawFinished,
    drawInProgress,
    showLines,
    showBackground,
    showStartButton,
  } = useSelector((state: any) => state.drawStage);
  const { muted, volumeLevel, zoomLevel, textSize, selectedCharacterItem } = useSelector(
    (state: any) => state.characters
  );
  const { pencilActive } = useSelector((state: any) => state.doodleStage);

  const onToggleMute = () => {
    dispatch(toggleMute());
    AudioManager.toggleMuteAll(!muted);
  };

  const onVolumeIncrease = () => {
    const newVolume = volumeLevel > 90 ? 100 : volumeLevel + 10;

    AudioManager.setVolume(newVolume);
    dispatch(setMute(false));
    dispatch(setVolume(newVolume));
  };

  const onVolumeDecrease = () => {
    const newVolume = volumeLevel < 10 ? 0 : volumeLevel - 10;

    AudioManager.setVolume(newVolume);
    dispatch(setMute(false));
    dispatch(setVolume(newVolume));
  };

  const onZoomIn = () => {
    const newZoom = zoomLevel > 190 ? 200 : zoomLevel + 10;

    dispatch(setZoom(newZoom));
  };

  const onZoomOut = () => {
    const newZoom = zoomLevel < 100 ? 100 : zoomLevel - 10;

    dispatch(setZoom(newZoom));
  };

  const onIncreaseFontSize = () => {
    const newTextSize = textSize > 290 ? 300 : textSize + 10;

    dispatch(setTextSize(newTextSize));
  };

  const onDecreaseFontSize = () => {
    const newTextSize = textSize < 100 ? 100 : textSize - 10;

    dispatch(setTextSize(newTextSize));
  };

  const handleShowLinesKeyPress = (event) => {
    event.preventDefault();
    if (event.key === 'Enter') {
      dispatch(toggleShowLines());
    }
  };

  const handleShowBackgroundKeyPress = (event) => {
    event.preventDefault();
    if (event.key === 'Enter') {
      dispatch(toggleShowBackground());
    }
  };

  const getShouldShowGrayBox = () => {
    const isCapitalPrint = selectedCharacterItem.characterCase === 'uppercase' && selectedCharacterItem.characterType === 'print';
    const isNumber = selectedCharacterItem.characterType === 'number';
    return isCapitalPrint || isNumber;
  }

  const shouldShowGrayBox = getShouldShowGrayBox();

  const renderGuideBlock = () => {
    const checkboxStyling = {
      color: '#2978c1',
      padding: '5px 5px 0px 10px',
    };

      const disabledCheckboxStyling = {
      color: '#00000061',
      padding: '5px 5px 0px 10px',
    };

    return (
      <fieldset className={fieldSet}>
        <legend className={menuItemTitle} role='heading' aria-level={3}>
          {t('guides')}
        </legend>
        {shouldShowGrayBox && (
          <Box className={menuItemSubtitle} role='heading' aria-level={4}>
            {t('atLeastOneGuide')}
          </Box>
        )}
        <FormControlLabel
          control={
            <Checkbox
              style={shouldShowGrayBox ? checkboxStyling : disabledCheckboxStyling}
              checked={showLines}
              onChange={() => dispatch(toggleShowLines())}
              onKeyPress={(event) => handleShowLinesKeyPress(event)}
              color='primary'
              disabled={!shouldShowGrayBox}
            />
          }
          label={t('showLines')}
          aria-label={tAria('showLinesCheckbox')}
        />
        {shouldShowGrayBox && (
          <FormControlLabel
            control={
              <Checkbox
                style={checkboxStyling}
                checked={showBackground}
                onChange={() => dispatch(toggleShowBackground())}
                onKeyPress={(event) => handleShowBackgroundKeyPress(event)}
                color='primary'
              />
            }
            label={t('showBlock')}
            aria-label={tAria('showBackgroundCheckbox')}
          />
        )}
      </fieldset>
    );
  };

  const maybeStyles: React.CSSProperties = pencilActive
    ? {
        backgroundColor: '#2e29c1',
        whiteSpace: 'nowrap',
      }
    : {};

  const renderToolsBlock = () => {
    return (
      <fieldset className={fieldSet}>
        <legend className={menuItemTitle} role='heading' aria-level={3}>
          {t('tools')}
        </legend>
        <Box className={buttonMenu}>
          <Button
            style={maybeStyles}
            className={`${button} ${blueButton}`}
            onClick={() => dispatch(togglePencilActivation())}
            variant='contained'
            color='primary'
            aria-label={pencilActive ? tAria('stopDrawingButton') : tAria('drawButton')}
            aria-pressed={pencilActive}
          >
            <FontAwesomeIcon icon={faPencilAlt} className={buttonIcon} />
            {pencilActive ? t('stopDraw') : t('draw')}
          </Button>
          {currentPathIndex === -1 ? (
            <Button
              className={`${button} ${outlineButton}`}
              onClick={() => dispatch(enableContinuousDraw())}
              disabled={showStartButton}
              variant='outlined'
              color='primary'
              aria-label={tAria('playAllButton')}
            >
              <FontAwesomeIcon icon={faPlay} className={buttonIcon} />
              {t('playAll')}
            </Button>
          ) : (
            <Button
              className={`${button} ${outlineButton}`}
              onClick={() => dispatch(clearAllPaths())}
              variant='outlined'
              color='primary'
              aria-label={tAria('stop')}
            >
              <FontAwesomeIcon icon={faStop} className={buttonIcon} />
              {t('stop')}
            </Button>
          )}
          {!drawInProgress && !drawFinished && (
            <Button
              className={`${button} ${outlineButton}`}
              onClick={() => dispatch(setNextPathIndex())}
              disabled={showStartButton}
              variant='outlined'
              color='primary'
              aria-label={tAria('playNextStrokeButton')}
            >
              <FontAwesomeIcon icon={faArrowRight} className={buttonIcon} />
              {t('playNextStroke')}
            </Button>
          )}
          <Button
            className={`${button} ${redButton}`}
            onClick={() => dispatch(eraseAll())}
            variant='contained'
            aria-label={tAria('eraseAllButton')}
          >
            <FontAwesomeIcon icon={faTrashAlt} className={buttonIcon} />
            {t('eraseAll')}
          </Button>
        </Box>
      </fieldset>
    );
  };

  const renderAccessibilityBlock = () => {
    return (
      <fieldset className={fieldSet}>
        <legend className={menuItemTitle} role='heading' aria-level={3}>
          {' '}
          {t('accessibility')}{' '}
        </legend>
        <Box className={accessibilityMenuGroup}>
          <Box className={menuItemSubtitle} role='heading' aria-level={4}>
            {t('audioControls')}
          </Box>
          <Box display='flex' flexDirection='row'>
            <PercentChooser
              value={volumeLevel}
              min={0}
              max={100}
              onIncrease={onVolumeIncrease}
              onDecrease={onVolumeDecrease}
              decreaseIcon={faVolumeDown}
              increaseIcon={faVolumeUp}
              ariaLabelDecreaseButton={tAria('volumeDecreaseButton')}
              ariaLabelIncreaseButton={tAria('volumeIncreaseButton')}
              ariaDescriptionCurrentValueHiddenDiv={tAria('currentVolume', {
                percent: volumeLevel,
              })}
              ariaDescriptionCurrentValue={tAria('volume')}
            />
            <IconButton
              onClick={onToggleMute}
              className={muteButton}
              aria-label={tAria(muted ? 'unmuteButton' : 'muteButton')}
            >
              <FontAwesomeIcon
                style={{ fontSize: 14 }}
                icon={muted ? faVolumeMute : faVolumeUp}
                size={'xs'}
              />
            </IconButton>
          </Box>
        </Box>
        <Box className={accessibilityMenuGroup}>
          <Box className={menuItemSubtitle} role='heading' aria-level={4}>
            {t('workspaceZoom')}
          </Box>
          <PercentChooser
            value={zoomLevel}
            min={100}
            max={200}
            onIncrease={onZoomIn}
            onDecrease={onZoomOut}
            decreaseIcon={faSearchMinus}
            increaseIcon={faSearchPlus}
            ariaLabelDecreaseButton={tAria('zoomOutButton')}
            ariaLabelIncreaseButton={tAria('zoomInButton')}
            ariaDescriptionCurrentValueHiddenDiv={tAria('currentZoom', { percent: zoomLevel })}
            ariaDescriptionCurrentValue={tAria('zoom')}
          />
        </Box>
        <Box className={accessibilityMenuGroup}>
          <Box className={menuItemSubtitle} role='heading' aria-level={4}>
            {t('closedCaptionsSize')}
          </Box>
          <PercentChooser
            value={textSize}
            min={100}
            max={300}
            onIncrease={onIncreaseFontSize}
            onDecrease={onDecreaseFontSize}
            decreaseIcon={faMinus}
            increaseIcon={faPlus}
            ariaLabelDecreaseButton={tAria('decreaseTextSizeButton')}
            ariaLabelIncreaseButton={tAria('increaseTextSizeButton')}
            ariaDescriptionCurrentValueHiddenDiv={tAria('currentTextSize', {
              percent: textSize,
            })}
            ariaDescriptionCurrentValue={tAria('textSize')}
          />
        </Box>
      </fieldset>
    );
  };

  return (
    <Box className={container}>
      <Box className={title} role='heading' aria-level={2}>
        {t('guidesAndTools')}
      </Box>
      <Box className={menuItems}>{renderGuideBlock()}</Box>
      <Box className={menuItems}>{renderToolsBlock()}</Box>
      <Box className={menuItems}>{renderAccessibilityBlock()}</Box>
    </Box>
  );
};

export default LNFMenu;

const useStyles = makeStyles({
  container: {
    fontFamily: 'Futura-Semibold',
  },
  title: {
    fontSize: '22px',
    margin: '20px 0px 10px 20px',
  },
  fieldSet: {
    display: 'contents',
  },
  menuItems: {
    border: '1px solid #cccccc',
    borderRadius: '5px',
    margin: '15px 15px',
    padding: '10px',
  },
  menuItemTitle: {
    fontWeight: 800,
    fontSize: '14px',
  },
  menuItemSubtitle: {
    fontFamily: 'Futura',
    fontWeight: 400,
    fontSize: '12px',
    paddingBottom: '5px',
  },
  buttonMenu: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  button: {
    width: '100%',
    maxWidth: '224px',
    minHeight: '40px',
    margin: '5px',
    textTransform: 'none',
  },
  blueButton: {
    backgroundColor: '#2978c1',
  },
  outlineButton: {
    border: '2px solid #2978c1',
  },
  redButton: {
    backgroundColor: '#d12348',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#7a142a',
    },
  },
  buttonIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '8px',
  },
  accessibilityMenuGroup: {
    marginBottom: '10px',
  },
  muteButton: {
    border: '2px solid #000000',
    borderRadius: '50%',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    marginLeft: '10px',
    color: '#000000',
  },
});
